import React from 'react';
import { Link } from 'react-router-dom';

const Terms = () => {
    return (
        <div className="container mx-auto px-4 py-12 max-w-4xl">
            {/* Header */}
            <div className="text-center mb-10">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-gradpath-green to-gradpath-dark rounded-2xl mb-4">
                    <i className="fas fa-file-contract text-white text-2xl"></i>
                </div>
                <h1 className="text-3xl md:text-4xl font-bold text-gradpath-dark mb-3">
                    Terms & Conditions
                </h1>
                <p className="text-gray-600">
                    Please read these terms carefully before using GradPath
                </p>
                <p className="text-sm text-gray-500 mt-2">
                    Last updated: June 1, 2026
                </p>
            </div>

            {/* Content */}
            <div className="bg-white rounded-xl shadow-md p-6 md:p-8 space-y-6">
                <section>
                    <h2 className="text-xl font-bold text-gradpath-dark mb-3 flex items-center gap-2">
                        <i className="fas fa-check-circle text-gradpath-green text-lg"></i>
                        Acceptance of Terms
                    </h2>
                    <p className="text-gray-700 leading-relaxed">
                        By accessing or using GradPath (the "Platform"), you agree to be bound by these Terms and Conditions. 
                        If you do not agree to these terms, please do not use our Platform.
                    </p>
                </section>

                <section>
                    <h2 className="text-xl font-bold text-gradpath-dark mb-3 flex items-center gap-2">
                        <i className="fas fa-user-plus text-gradpath-green text-lg"></i>
                        Account Registration
                    </h2>
                    <p className="text-gray-700 leading-relaxed mb-3">
                        To use certain features of our Platform, you must register for an account. You agree to:
                    </p>
                    <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                        <li>Provide accurate, current, and complete information</li>
                        <li>Maintain the security of your password and account</li>
                        <li>Accept responsibility for all activities under your account</li>
                        <li>Notify us immediately of any unauthorized use</li>
                    </ul>
                    <p className="text-gray-700 leading-relaxed mt-3">
                        You must be at least 18 years old to create an account.
                    </p>
                </section>

                <section>
                    <h2 className="text-xl font-bold text-gradpath-dark mb-3 flex items-center gap-2">
                        <i className="fas fa-briefcase text-gradpath-green text-lg"></i>
                        Job Applications
                    </h2>
                    <p className="text-gray-700 leading-relaxed">
                        GradPath is a job aggregation platform that redirects users to external job postings. 
                        We are not responsible for:
                    </p>
                    <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4 mt-3">
                        <li>The application process or hiring decisions of employers</li>
                        <li>The content, accuracy, or legitimacy of external job postings</li>
                        <li>Any issues arising from your communication with employers</li>
                    </ul>
                    <p className="text-gray-700 leading-relaxed mt-3">
                        You should verify all job information independently before applying.
                    </p>
                </section>

                <section>
                    <h2 className="text-xl font-bold text-gradpath-dark mb-3 flex items-center gap-2">
                        <i className="fas fa-code-branch text-gradpath-green text-lg"></i>
                        User Conduct
                    </h2>
                    <p className="text-gray-700 leading-relaxed mb-3">
                        You agree not to misuse the Platform, including but not limited to:
                    </p>
                    <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                        <li>Attempting to access unauthorized areas</li>
                        <li>Interfering with other users' experience</li>
                        <li>Uploading malicious code or viruses</li>
                        <li>Scraping or harvesting data without permission</li>
                        <li>Impersonating another person or entity</li>
                        <li>Violating any applicable laws or regulations</li>
                    </ul>
                </section>

                <section>
                    <h2 className="text-xl font-bold text-gradpath-dark mb-3 flex items-center gap-2">
                        <i className="fas fa-copyright text-gradpath-green text-lg"></i>
                        Intellectual Property
                    </h2>
                    <p className="text-gray-700 leading-relaxed">
                        All content on GradPath, including text, graphics, logos, and software, is the property of 
                        GradPath or its content suppliers and is protected by South African and international copyright laws. 
                        You may not reproduce, distribute, or create derivative works without our express written permission.
                    </p>
                </section>

                <section>
                    <h2 className="text-xl font-bold text-gradpath-dark mb-3 flex items-center gap-2">
                        <i className="fas fa-exclamation-triangle text-gradpath-green text-lg"></i>
                        Limitation of Liability
                    </h2>
                    <p className="text-gray-700 leading-relaxed">
                        GradPath is provided "as is" without warranties of any kind. To the maximum extent permitted by law, 
                        we are not liable for any damages arising from your use of the Platform, including but not limited to:
                    </p>
                    <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4 mt-3">
                        <li>Loss of data or employment opportunities</li>
                        <li>Indirect, incidental, or consequential damages</li>
                        <li>Damages resulting from third-party services</li>
                    </ul>
                </section>

                <section>
                    <h2 className="text-xl font-bold text-gradpath-dark mb-3 flex items-center gap-2">
                        <i className="fas fa-ban text-gradpath-green text-lg"></i>
                        Termination
                    </h2>
                    <p className="text-gray-700 leading-relaxed">
                        We reserve the right to suspend or terminate your account at our sole discretion, without notice, 
                        for conduct that violates these Terms or is harmful to other users or the Platform.
                    </p>
                </section>

                <section>
                    <h2 className="text-xl font-bold text-gradpath-dark mb-3 flex items-center gap-2">
                        <i className="fas fa-gavel text-gradpath-green text-lg"></i>
                        Governing Law
                    </h2>
                    <p className="text-gray-700 leading-relaxed">
                        These Terms shall be governed by and construed in accordance with the laws of the Republic of South Africa. 
                        Any disputes arising under these Terms shall be subject to the exclusive jurisdiction of the courts of South Africa.
                    </p>
                </section>

                <section>
                    <h2 className="text-xl font-bold text-gradpath-dark mb-3 flex items-center gap-2">
                        <i className="fas fa-edit text-gradpath-green text-lg"></i>
                        Changes to Terms
                    </h2>
                    <p className="text-gray-700 leading-relaxed">
                        We may modify these terms at any time. Changes become effective immediately upon posting. 
                        Your continued use of the Platform after changes constitutes acceptance of the updated terms.
                    </p>
                </section>

                <section className="bg-gray-50 p-4 rounded-lg">
                    <h2 className="text-lg font-bold text-gradpath-dark mb-2 flex items-center gap-2">
                        <i className="fas fa-envelope text-gradpath-green"></i>
                        Contact Information
                    </h2>
                    <p className="text-gray-700 text-sm">
                        For questions about these Terms, please contact us at:
                        <a href="mailto:support@gradpath.co.za" className="text-gradpath-green hover:underline block mt-1">
                            support@gradpath.co.za
                        </a>
                    </p>
                </section>

                {/* Back to Home Link */}
                <div className="text-center pt-4">
                    <Link 
                        to="/" 
                        className="inline-flex items-center gap-2 text-gradpath-green hover:underline"
                    >
                        <i className="fas fa-arrow-left"></i>
                        Back to Home
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default Terms;